import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Project column for the global quote / invoice / proposal lists.
 *
 * Linked   → clickable project name.
 * Unlinked → the free-text project name typed on the document (greyed out),
 *            plus a picker to assign it to a real project.
 *
 * `collection` is the API path segment: 'quotes' | 'invoices' | 'proposals'.
 */
export default function ProjectLinkCell({ docId, collection, projectId, fallbackText, projects, onLinked }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(false);

  const linked = projects.find(p => p.id === projectId);

  if (projectId && linked) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}`); }}
        className="flex items-center gap-1 text-sm truncate text-[#1a1a2e] hover:text-[#c9a84c] transition-colors"
        title={`Go to ${linked.project_name}`}
      >
        <FolderKanban className="w-3.5 h-3.5 flex-shrink-0 text-[#c9a84c]" />
        <span className="truncate">{linked.project_name}</span>
      </button>
    );
  }

  const assign = async (value) => {
    if (!value) { setPicking(false); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/${collection}/${docId}/project`, { project_id: value });
      onLinked(docId, value);
    } catch (err) {
      console.error('Failed to link project:', err);
    }
    setSaving(false);
    setPicking(false);
  };

  if (picking) {
    return (
      <div onClick={e => e.stopPropagation()}>
        <select
          autoFocus
          disabled={saving}
          defaultValue=""
          onChange={e => assign(e.target.value)}
          onBlur={() => setPicking(false)}
          className="w-full text-xs border rounded px-1.5 py-1 bg-white"
          style={{ borderColor: 'rgba(201,168,76,0.4)' }}
        >
          <option value="">Select project…</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.project_name}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 min-w-0" onClick={e => e.stopPropagation()}>
      <span className="text-sm truncate text-[#9ca3af]" title={fallbackText || 'No project name'}>
        {fallbackText || '—'}
      </span>
      <button
        onClick={() => setPicking(true)}
        disabled={saving}
        className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded border text-[#6b7280] hover:text-[#c9a84c] hover:border-[#c9a84c] transition-colors disabled:opacity-40"
        style={{ borderColor: 'rgba(201,168,76,0.3)' }}
        title="Assign to a project"
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Assign'}
      </button>
    </div>
  );
}
